/**
 * One return's three stages — prepared, uploaded to the portal, filed — as the
 * Overview shows them.
 * @file src/web/features/admin/lib/filing-track.ts
 *
 * Pulled out of the Overview so the words and the done/not-done of each stage
 * can be tested on the cases that went wrong: GSTR-1 filed with an ARN beside a
 * GSTR-3B that was uploaded and never filed; a "filed" record with no ARN; an
 * ARN typed in and never returned by the portal. Pure; the clock is passed in.
 *
 * @llm-rule WHEN: Saying where a return stands on the Overview
 * @llm-rule AVOID: Building 'prepared / uploaded / filed' text inline in a component
 */
import { isFiled, filedClaimed, filedUnconfirmed, filedWithoutArn, stageOf, VERIFY_ON_PORTAL, type FilingStatusRow } from './filing-status';
import { freshness, disagreement, type PortalEntry, type Freshness } from './portal-status';
import { dueStatus, dueText } from './due-dates';

export interface TrackRow extends FilingStatusRow {
  createdAt: string;
  filedAt: string | null;
}

const day = (iso: string) => new Date(iso).toLocaleDateString('en-IN', { dateStyle: 'medium' });

export interface FilingState { state: string; tone: 'ok' | 'warn' | 'neutral'; detail: string }

/** The one-line state of a return and the sentence under it. */
export function filingState(rows: TrackRow[], uploadedAt: string | null): FilingState {
  const filed = rows.find(isFiled);
  if (filed) return { state: 'Filed', tone: 'ok', detail: `ARN ${filed.arn}` };
  const unconfirmed = rows.find(filedUnconfirmed);
  if (unconfirmed) return { state: 'Filed, unconfirmed', tone: 'warn', detail: `${VERIFY_ON_PORTAL} (ARN ${unconfirmed.arn}).` };
  if (rows.some(filedWithoutArn)) {
    return { state: 'Filed, no ARN', tone: 'warn', detail: 'Marked filed here without an ARN, so the portal has not acknowledged it. Check the portal and record the ARN.' };
  }
  if (stageOf(rows, uploadedAt) === 'uploaded') {
    return { state: 'Uploaded, not filed', tone: 'warn', detail: `Uploaded to the portal ${day(uploadedAt!)}. Not filed — it has no ARN until it is signed.` };
  }
  const prepared = rows.find((r) => r.status === 'prepared');
  if (prepared) return { state: 'Prepared', tone: 'warn', detail: `Prepared ${day(prepared.createdAt)}, not filed.` };
  return { state: 'Not started', tone: 'neutral', detail: 'No prepared record for this period yet.' };
}

export interface TrackStage {
  title: string;
  detail: string;
  done: boolean;
  due?: string | null;
  checked?: Freshness | null;
  warning?: string | null;
}

export interface TrackInput {
  /** As the return is named on screen. */
  label: 'GSTR-1' | 'GSTR-3B';
  period: string;
  /** Day of the following month the return falls due (11 for GSTR-1, 20 for GSTR-3B). */
  dueDay: number;
  quarterly: boolean;
  rows: TrackRow[];
  entry?: PortalEntry;
  now: Date;
  /** What to say when nothing has been prepared. */
  unpreparedDetail: string;
  /** Appended to the prepared stage's detail when it has been (the 3B says where its credit came from). */
  preparedSuffix?: string;
  /** What to say under 'filed' when it is not. */
  notFiledDetail: string;
}

export function filingTrack(a: TrackInput): { state: FilingState; prepared: TrackStage; uploaded: TrackStage; filed: TrackStage } {
  const uploadedAt = a.entry?.uploadedAt ?? null;
  const state = filingState(a.rows, uploadedAt);
  const isDone = a.rows.some(isFiled);
  const claimed = a.rows.some(filedClaimed);

  const filedRow = a.rows.find(isFiled);
  /* Late to the day it was filed for a return that is filed; to today for one
     that is not. A claim is not a filing, so it keeps counting. */
  const due = a.quarterly
    ? 'Quarterly filer (QRMP) — due dates differ.'
    : dueText(dueStatus({
      period: a.period, day: a.dueDay, today: a.now.toLocaleDateString('en-CA'),
      filedOn: filedRow?.filedAt ? new Date(filedRow.filedAt).toISOString().slice(0, 10) : null,
    }));

  return {
    state,
    prepared: {
      title: `${a.label} prepared`,
      detail: a.rows.length ? `${state.detail}${a.preparedSuffix ? ` · ${a.preparedSuffix}` : ''}` : a.unpreparedDetail,
      done: a.rows.length > 0,
    },
    uploaded: {
      title: `${a.label} uploaded to the portal`,
      detail: uploadedAt ? `Uploaded ${day(uploadedAt)}. Uploaded is not filed.` : isDone ? 'Filed, so it went up.' : 'Not uploaded from oddits yet.',
      done: !!uploadedAt || isDone,
    },
    filed: {
      title: `${a.label} filed`,
      detail: isDone || claimed ? state.detail : a.notFiledDetail,
      done: isDone,
      due,
      checked: freshness(a.entry?.checkedAt, a.now),
      warning: disagreement(a.rows, a.entry, a.now),
    },
  };
}
